'use client';
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuthContext } from './AuthProvider';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';

const API_BASE = `${process.env.NEXT_PUBLIC_LOCAL_API}/api`; 

// ЗАГЛУШКА: socket.io отключён полностью.
// Тип оставлен как раньше (socket: null), чтобы остальной код, который дергает useSocket(),
// не падал — просто ничего реального под капотом больше нет.
interface SocketContextType {
  socket: null;
  unreadMsgs: number;
  unreadNotifs: number;
  setUnreadMsgs: React.Dispatch<React.SetStateAction<number>>;
  setUnreadNotifs: React.Dispatch<React.SetStateAction<number>>;
  isSoundMuted: boolean;
  toggleSound: () => void;
  isConnected: boolean; // всегда false — сокета нет
  connectSocket: () => void; // no-op, оставлен только чтобы не ломать вызовы из других компонентов
  disconnectSocket: () => void; // no-op
}

const SocketContext = createContext<SocketContextType>({} as SocketContextType);
export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuthContext();
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [isSoundMuted, setIsSoundMuted] = useState(false);

  const popSound = useRef<HTMLAudioElement | null>(null);
  const [toast, setToast] = useState<{ id: number, title: string, text: string } | null>(null);

  const targetId = user?.oauthId || user?.id;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      popSound.current = new Audio('/sounds/pop.mp3');
      const savedMute = localStorage.getItem('chat_muted') === 'true';
      setIsSoundMuted(savedMute);
    }
  }, []);

  const toggleSound = () => {
    setIsSoundMuted(prev => {
      localStorage.setItem('chat_muted', String(!prev));
      return !prev;
    });
  };

  // Единственный источник данных о непрочитанном — обычный REST-запрос, без сокета и без realtime.
  useEffect(() => {
    if (!isAuthenticated || !targetId) return;
    fetch(`${API_BASE}/inbox/unread/${targetId}`)
      .then(res => res.json())
      .then(data => {
        setUnreadMsgs(data.messages || 0);
        setUnreadNotifs(data.notifications || 0);
      }).catch(() => {});
  }, [isAuthenticated, targetId]);

  // no-op заглушки — socket.io больше нигде не импортируется и не подключается
  const connectSocket = () => {};
  const disconnectSocket = () => {};

  return (
    <SocketContext.Provider
      value={{
        socket: null,
        unreadMsgs,
        unreadNotifs,
        setUnreadMsgs,
        setUnreadNotifs,
        isSoundMuted,
        toggleSound,
        isConnected: false,
        connectSocket,
        disconnectSocket,
      }}
    >
      {children}

      {/* Экран "переподключение" убран — раз сокета нет, показывать его нечего */}

      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast.id}
            initial={{ y: -100, opacity: 0, scale: 0.9 }}
            animate={{ y: 20, opacity: 1, scale: 1 }}
            exit={{ y: -100, opacity: 0, scale: 0.9 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999999] bg-[#1a1a1d] border border-white/10 shadow-2xl rounded-2xl p-3 flex items-center gap-4 cursor-pointer min-w-[300px]"
            onClick={() => {
              setToast(null);
              window.dispatchEvent(new Event('open-messenger'));
            }}
          >
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-500/20">
              <MessageCircle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-bold text-sm">{toast.title}</h4>
              <p className="text-zinc-400 text-xs truncate">{toast.text}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); setToast(null); }} className="text-zinc-500 hover:text-white p-1">
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </SocketContext.Provider>
  );
};
